import type { RunRowLike } from "@/types/RunRow"

export const DEFAULT_SIMILARITY_THRESHOLD = 0.5

const STOPWORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "as",
  "that",
  "than",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "am",
  "has",
  "have",
  "had",
  "having",
  "of",
  "in",
  "on",
  "at",
  "to",
  "for",
  "with",
  "by",
  "from",
  "into",
  "onto",
  "it",
  "its",
  "this",
  "these",
  "those",
  "i",
  "me",
  "my",
  "we",
  "us",
  "our",
  "you",
  "your",
  "yours",
  "he",
  "him",
  "his",
  "she",
  "her",
  "hers",
  "they",
  "them",
  "their",
  "theirs",
  "s",
  "t",
  "d",
  "ll",
  "re",
  "ve",
  "m",
])

function stem(token: string): string {
  if (token.length <= 3) {
    return token
  }
  if (token.endsWith("ies") && token.length >= 5) {
    return `${token.slice(0, -3)}y`
  }
  if (token.endsWith("sses")) {
    return token.slice(0, -2)
  }
  if (token.endsWith("ing") && token.length >= 6) {
    return token.slice(0, -3)
  }
  if (token.endsWith("ed") && token.length >= 5) {
    return token.slice(0, -2)
  }
  if (token.endsWith("ly") && token.length >= 5) {
    return token.slice(0, -2)
  }
  if (
    token.endsWith("s") &&
    !token.endsWith("ss") &&
    !token.endsWith("us") &&
    token.length >= 4
  ) {
    return token.slice(0, -1)
  }
  return token
}

function normalizeAndTokenize(text: string): Set<string> {
  const cleaned = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .trim()
  if (!cleaned) {
    return new Set<string>()
  }
  return new Set(
    cleaned
      .split(/\s+/)
      .filter((token) => !STOPWORDS.has(token))
      .map(stem)
      .filter(Boolean),
  )
}

export function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) {
    return 0
  }
  let intersection = 0
  for (const token of a) {
    if (b.has(token)) {
      intersection++
    }
  }
  const union = a.size + b.size - intersection
  return union === 0 ? 0 : intersection / union
}

interface GroupRowsByExplanationSimilarityParams {
  rows: RunRowLike[]
  getExplanation: (row: RunRowLike) => string
  threshold?: number
}

export function groupRowsByExplanationSimilarity({
  rows,
  getExplanation,
  threshold = DEFAULT_SIMILARITY_THRESHOLD,
}: GroupRowsByExplanationSimilarityParams): RunRowLike[] {
  if (rows.length <= 1) {
    return rows
  }

  const tokens = rows.map((row) => normalizeAndTokenize(getExplanation(row)))

  const parent = Array.from({ length: rows.length }, (_, i) => i)
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]]
      x = parent[x]
    }
    return x
  }
  const union = (a: number, b: number) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) {
      parent[ra] = rb
    }
  }

  for (let i = 0; i < rows.length; i++) {
    if (tokens[i].size === 0) {
      continue
    }
    for (let j = i + 1; j < rows.length; j++) {
      if (tokens[j].size === 0) {
        continue
      }
      if (jaccardSimilarity(tokens[i], tokens[j]) >= threshold) {
        union(i, j)
      }
    }
  }

  const clusterOrder: number[] = []
  const clusterMembers = new Map<number, number[]>()
  const emptyIndices: number[] = []

  for (let i = 0; i < rows.length; i++) {
    if (tokens[i].size === 0) {
      emptyIndices.push(i)
      continue
    }
    const root = find(i)
    const existing = clusterMembers.get(root)
    if (existing) {
      existing.push(i)
    } else {
      clusterOrder.push(root)
      clusterMembers.set(root, [i])
    }
  }

  const reordered: RunRowLike[] = []
  for (const root of clusterOrder) {
    const members = clusterMembers.get(root) ?? []
    for (const idx of members) {
      reordered.push(rows[idx])
    }
  }
  for (const idx of emptyIndices) {
    reordered.push(rows[idx])
  }
  return reordered
}
